/**
 * This browser as a device of the account (master `devices` +
 * `account_devices`, listed by the settings page System › Devices).
 *
 * Before a password login the shell registers the browser with
 * `POST /v1/devices` and names the returned device on `/oauth/login`
 * (`deviceUUID`); master binds the new session to it and links it to the
 * account (backend/master/api/public/account_devices.go,
 * `bindLoginBrowserDevice`). A stable serial per browser makes every later
 * login find the same device instead of creating a new one, so the serial is
 * kept on sign-out.
 *
 * Deliberately free of shell imports: `auth.ts` and `settingsHub/devices.ts` both
 * use it, and neither may pull the other in. Only the shared locale helper of
 * `@blpde/connect` comes in, so the registration carries the UI language.
 */
import { localeHeader } from '@blpde/connect/http';

/** The browser's serial. Survives sign-out (`clearAuth` does not know it). */
export const DEVICE_SERIAL_STORAGE_KEY = 'formica.connect.deviceSerial';

/** The application key the Connect shell registers its browser devices under. */
const DEVICE_APPLICATION_KEY = 'connect';

/** The registration must not hold the login up for long: it is best-effort. */
const REGISTER_TIMEOUT_MS = 3_000;

/** Browser and system of a user agent, as far as it names them („Chrome“, „macOS“). */
export function describeUserAgent(userAgent: string): { browser: string; system: string } {
  const ua = userAgent || '';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\/|CriOS\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : '';
  const system = /iPhone|iPad|iPod/.test(ua)
    ? 'iOS'
    : /Android/.test(ua)
      ? 'Android'
      : /Windows/.test(ua)
        ? 'Windows'
        : /Mac OS X|Macintosh/.test(ua)
          ? 'macOS'
          : /CrOS/.test(ua)
            ? 'ChromeOS'
            : /Linux/.test(ua)
              ? 'Linux'
              : '';
  return { browser, system };
}

/**
 * The stored device name („Chrome · macOS“). Language-neutral on purpose: it
 * is data other people read too (admin device lists); the settings page
 * renders its own localized caption from the user agent.
 */
export function browserDeviceName(userAgent: string): string {
  const { browser, system } = describeUserAgent(userAgent);
  return [browser, system].filter(Boolean).join(' · ') || 'Browser';
}

/** A random RFC 4122 v4 uuid. `crypto.randomUUID` needs a secure context; a LAN demo over http has none. */
function randomUUID(): string {
  if (typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      // Insecure context: fall through to getRandomValues, which works everywhere.
    }
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * The browser's stable serial, created on first use. null when storage is
 * unavailable (private mode, blocked site data): without a serial that
 * survives, every login would register a new device, so none is registered.
 */
export function browserDeviceSerial(): string | null {
  try {
    const stored = localStorage.getItem(DEVICE_SERIAL_STORAGE_KEY);
    if (stored) return stored;
    const serial = randomUUID();
    localStorage.setItem(DEVICE_SERIAL_STORAGE_KEY, serial);
    return localStorage.getItem(DEVICE_SERIAL_STORAGE_KEY) === serial ? serial : null;
  } catch {
    return null;
  }
}

/**
 * Registers this browser with master (`POST <apiBase>/devices`, which answers
 * the existing device for a known serial) and returns the device uuid to name
 * on the login. null on any failure — the login then runs without a device.
 */
export async function registerBrowserDevice(apiBase: string): Promise<string | null> {
  const serial = browserDeviceSerial();
  if (!serial) return null;
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  try {
    const res = await fetch(`${apiBase}/devices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...localeHeader() },
      body: JSON.stringify({
        name: browserDeviceName(userAgent),
        serial,
        application: DEVICE_APPLICATION_KEY,
        browser: { user_agent: userAgent },
      }),
      signal: AbortSignal.timeout(REGISTER_TIMEOUT_MS),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { uuid?: unknown };
    return typeof body.uuid === 'string' && body.uuid ? body.uuid : null;
  } catch {
    return null;
  }
}
